import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form, Message, Table, Label } from 'semantic-ui-react';
import { request, yuan, time, states } from './helpers';
import './platform.css';

export default function Wallet() {
  const [wallet, setWallet] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [entries, setEntries] = useState([]);
  const [calls, setCalls] = useState([]);
  const [payments, setPayments] = useState([]);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState('models');
  const [page, setPage] = useState(0);
  const latest = useRef(0);
  const activePage = useRef(page);
  activePage.current = page;
  const load = useCallback(async () => {
    const snapshot = activePage.current;
    const version = ++latest.current;
    try {
      const data = await Promise.all([
        request('get', '/api/platform/wallet'),
        request('get', '/api/platform/catalog'),
        request('get', `/api/platform/entries?page=${snapshot}`),
        request('get', `/api/platform/requests?page=${snapshot}`),
        request('get', '/api/platform/payments'),
      ]);
      if (version !== latest.current || snapshot !== activePage.current) return;
      setWallet(data[0]);
      setCatalog(data[1]);
      setEntries(data[2]);
      setCalls(data[3]);
      setPayments(data[4]);
      setError('');
    } catch (e) {
      if (version === latest.current && snapshot === activePage.current)
        setError(e.message);
    }
  }, []);
  useEffect(() => {
    load();
    return () => {
      latest.current++;
    };
  }, [load, page]);
  async function redeem() {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const amount = await request('post', '/api/user/topup', {
        key: code.trim(),
      });
      setCode('');
      setNotice(`充值成功，到账 ${yuan(amount)}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className='platform-shell'>
      <div className='platform-heading'>
        <div>
          <div className='platform-eyebrow'>ATC / 开发者控制台</div>
          <h1>模型与钱包</h1>
          <p>统一接入模型，用量与费用逐笔可查。</p>
        </div>
        <Button onClick={load}>刷新</Button>
      </div>
      {error && <Message negative content={error} />}
      {notice && <Message positive content={notice} />}
      <div className='platform-stats'>
        <section>
          <span>可用余额 · CNY</span>
          <strong>{yuan(wallet.available)}</strong>
          <small>充值后可用于模型调用</small>
        </section>
        <section>
          <span>冻结金额</span>
          <strong>{yuan(wallet.held)}</strong>
          <small>调用预占与待核算金额</small>
        </section>
        <section>
          <span>累计消费</span>
          <strong>{yuan(wallet.used)}</strong>
          <small>以已结算的实际用量为准</small>
        </section>
      </div>
      <div className='platform-tabs'>
        {[
          ['models', '模型目录'],
          ['recharge', '充值'],
          ['calls', '调用账单'],
          ['entries', '钱包流水'],
          ['docs', '接入指南'],
        ].map(([id, name]) => (
          <button
            key={id}
            className={tab === id ? 'active' : ''}
            onClick={() => {
              setTab(id);
              setPage(0);
            }}
          >
            {name}
          </button>
        ))}
      </div>
      <section className='platform-panel'>
        {tab === 'models' && (
          <>
            <h2>可用模型</h2>
            <p>价格单位：人民币 / 百万 Token。模型已定价后还需配置可用渠道。</p>
            <div className='platform-models'>
              {catalog.map((p) => (
                <article key={p.id}>
                  <Label>{p.provider}</Label>
                  {p.test_price && <Label color='orange'>测试价格</Label>}
                  <h3>{p.model}</h3>
                  <p>
                    {p.kind === 'embeddings'
                      ? '文本向量'
                      : p.kind === 'responses'
                      ? 'Codex · OpenAI Responses'
                      : p.vision
                      ? '对话 · 图片理解'
                      : '文本对话'}
                  </p>
                  <dl>
                    <dt>输入</dt>
                    <dd>{yuan(p.input_rate)}</dd>
                    {p.kind === 'responses' && (
                      <>
                        <dt>Cache Write</dt>
                        <dd>{yuan(p.cache_write_rate)}</dd>
                        <dt>Cache Read</dt>
                        <dd>{yuan(p.cache_read_rate)}</dd>
                      </>
                    )}
                    <dt>输出</dt>
                    <dd>{yuan(p.output_rate)}</dd>
                  </dl>
                  <small>
                    输入上限 {p.max_input_tokens.toLocaleString()}
                    {p.kind === 'chat' && (
                      <>
                        {' '}
                        · 默认输出 {p.default_output_tokens.toLocaleString()}
                      </>
                    )}{' '}
                    · 输出上限 {p.max_output_tokens.toLocaleString()}
                  </small>
                </article>
              ))}
            </div>
            {!catalog.length && (
              <Message content='暂无启用模型，请联系管理员配置渠道与价格。' />
            )}
          </>
        )}
        {tab === 'recharge' && (
          <>
            <h2>为钱包充值</h2>
            <div className='platform-payments'>
              {payments.map((p) => (
                <article key={p.id}>
                  <strong>{p.name}</strong>
                  <p>
                    {p.admin_only
                      ? '联系管理员入账'
                      : p.enabled
                      ? '已启用'
                      : p.reason}
                  </p>
                </article>
              ))}
            </div>
            <Form onSubmit={redeem}>
              <Form.Input
                label='兑换码'
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder='输入兑换码'
              />
              <Button
                primary
                type='submit'
                loading={busy}
                disabled={busy || !code.trim()}
              >
                兑换并入账
              </Button>
            </Form>
          </>
        )}
        {tab === 'calls' && (
          <>
            <h2>调用账单</h2>
            <div className='platform-scroll'>
              <Table unstackable basic='very' striped>
                <Table.Header>
                  <Table.Row>
                    {[
                      '时间',
                      '模型',
                      '状态',
                      '输入 / Cache Write / Cache Read / 输出',
                      '思考 Token',
                      '消费',
                      '价格版本 / 四类单价',
                      '请求 ID',
                    ].map((s) => (
                      <Table.HeaderCell key={s}>{s}</Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {calls.map((c) => (
                    <Table.Row key={c.id}>
                      <Table.Cell>{time(c.created_at)}</Table.Cell>
                      <Table.Cell>{c.model}</Table.Cell>
                      <Table.Cell>
                        <Label
                          color={c.state === 'pending' ? 'orange' : undefined}
                        >
                          {states[c.state]}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {c.prompt_tokens -
                          c.cache_write_tokens -
                          c.cache_read_tokens}{' '}
                        / {c.cache_write_tokens} / {c.cache_read_tokens} /{' '}
                        {c.completion_tokens}
                      </Table.Cell>
                      <Table.Cell>{c.reasoning_tokens}</Table.Cell>
                      <Table.Cell>{yuan(c.charged)}</Table.Cell>
                      <Table.Cell>
                        #{c.price_id}
                        <br />
                        {yuan(c.input_rate)} / {yuan(c.cache_write_rate)} /{' '}
                        {yuan(c.cache_read_rate)} / {yuan(c.output_rate)} 每百万
                        Token
                      </Table.Cell>
                      <Table.Cell>
                        <code>{c.request_id}</code>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
            <p>
              待核算调用保留冻结金额，核算后再结算或释放。思考 Token
              已包含在输出用量内。
            </p>
          </>
        )}
        {tab === 'entries' && (
          <>
            <h2>钱包流水</h2>
            <div className='platform-scroll'>
              <Table unstackable basic='very' striped>
                <Table.Header>
                  <Table.Row>
                    {['时间', '类型', '金额', '变更后余额', '说明'].map((s) => (
                      <Table.HeaderCell key={s}>{s}</Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {entries.map((e) => (
                    <Table.Row key={e.id}>
                      <Table.Cell>{time(e.created_at)}</Table.Cell>
                      <Table.Cell>
                        {{
                          credit: '充值',
                          reserve: '预占',
                          release: '释放',
                          consume: '消费',
                          reconcile: '人工核算',
                        }[e.kind] || e.kind}
                      </Table.Cell>
                      <Table.Cell>{yuan(e.amount)}</Table.Cell>
                      <Table.Cell>{yuan(e.balance)}</Table.Cell>
                      <Table.Cell>{e.reason}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </>
        )}
        {(tab === 'calls' || tab === 'entries') && (
          <div>
            <Button disabled={page === 0} onClick={() => setPage(page - 1)}>
              上一页
            </Button>
            <span>第 {page + 1} 页</span>
            <Button
              disabled={(tab === 'calls' ? calls : entries).length < 50}
              onClick={() => setPage(page + 1)}
            >
              下一页
            </Button>
          </div>
        )}
        {tab === 'docs' && (
          <>
            <h2>使用平台 API Key</h2>
            <p>
              在 <Link to='/token'>API Key 管理</Link>{' '}
              创建令牌，使用平台地址和目录中的模型名称。调用前会按输入上限和请求的输出上限预占余额，实际消费结算后释放剩余金额。
            </p>
            <pre>{`from openai import OpenAI\nimport os\n\nclient = OpenAI(\n    api_key=os.environ["ATC_API_KEY"],\n    base_url="${window.location.origin}/v1",\n)\n\nresponse = client.chat.completions.create(\n    model="glm-5.3",\n    messages=[{"role": "user", "content": "你好"}],\n    max_tokens=256,\n)\nprint(response.choices[0].message.content)\n\nvector = client.embeddings.create(\n    model="embedding-3", input="用于检索的文本",\n)`}</pre>
            <p>
              图片理解使用支持该能力的模型，在 messages 中提供
              image_url。测试价格仅用于本地验证，正式销售前由管理员重新定价。
            </p>
            <h3>Claude Code</h3>
            <p>
              本平台支持 Anthropic Messages 基础兼容。使用平台 Key
              配置以下环境变量，Base URL 不加 /v1。
            </p>
            <pre>{`unset ANTHROPIC_AUTH_TOKEN CLAUDE_CODE_OAUTH_TOKEN
export ANTHROPIC_BASE_URL="${window.location.origin}"
export ANTHROPIC_API_KEY="sk-你的平台Key"
export ANTHROPIC_MODEL="glm-5.3"
export ANTHROPIC_DEFAULT_SONNET_MODEL="glm-5.3"
export ANTHROPIC_DEFAULT_OPUS_MODEL="glm-5.3"
export ANTHROPIC_DEFAULT_HAIKU_MODEL="glm-5.3"
export CLAUDE_CODE_MAX_OUTPUT_TOKENS="2048"
export CLAUDE_CODE_MAX_CONTEXT_TOKENS="100000"
export CLAUDE_CODE_DISABLE_EXPERIMENTAL_BETAS="1"
export ENABLE_TOOL_SEARCH="false"
claude`}</pre>
            <p>
              客户端工具在本机执行。服务端搜索工具、文档块和签名思考块暂不支持；费用以平台人民币账单为准。工具定义较大时，请管理员配置足够的输入上限。
            </p>
          </>
        )}
      </section>
    </main>
  );
}
